import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  FileSignature, Plus, RefreshCw, Search, X,
  ChevronLeft, ChevronRight, CheckCircle2, Clock,
  XCircle, Save, AlertTriangle,
} from 'lucide-react';

import { chequeIssuanceApi } from '@/api/chequeIssuance.api';
import type { ChequeAwaitingIssuance } from '@/api/chequeIssuance.api';
import { usePermission }      from '@/hooks/usePermission';
import { Permissions }        from '@/constants/permissions';
import { Spinner }            from '@/components/ui/Spinner';
import { cn }                 from '@/utils/cn';
import type { RootState }     from '@/state/store';

import type {
  ChequeIssuanceDto,
  ChequeIssuanceListDto,
  ChequeIssuanceFilterDto,
} from '@/types/chequeIssuance.types';
import {
  CHEQUE_ISSUANCE_STATUS_COLORS,
  CHEQUE_ISSUANCE_STATUS_LABELS,
} from '@/types/chequeIssuance.types';

// ── Status icon ───────────────────────────────────────────────────────────────
const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'Collected':  return <CheckCircle2 size={14} className="text-green-500 shrink-0" />;
    case 'Issued':     return <CheckCircle2 size={14} className="text-blue-500 shrink-0" />;
    case 'Cancelled':  return <XCircle      size={14} className="text-red-500 shrink-0" />;
    default:           return <Clock        size={14} className="text-yellow-500 shrink-0" />;
  }
};

// ── ID type options ───────────────────────────────────────────────────────────
const ID_TYPES = ['NIC', 'Passport', 'Driving License', 'Other'];

// ── Page ──────────────────────────────────────────────────────────────────────
export const ChequeIssuancePage = () => {
  const { hasPermission } = usePermission();
  const companyId         = useSelector((s: RootState) => s.session.session?.companyId ?? '');

  const canCreate = hasPermission((Permissions as any).BNK_CHEQUE_ISSUE);

  // ── List state ────────────────────────────────────────────────────────────
  const [items,   setItems]   = useState<ChequeIssuanceListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const [statusFilter,    setStatusFilter]    = useState<string | undefined>(undefined);
  const [dateFrom,        setDateFrom]        = useState('');
  const [dateTo,          setDateTo]          = useState('');
  const [collectorSearch, setCollectorSearch] = useState('');
  /** Cheque number, payee, or payment voucher reference (GAP-015). */
  const [search,          setSearch]          = useState('');
  /** PRINTED / NOT_PRINTED / '' (either) — the "Pending Issue vs Printed" split the requirement
   *  asks for, distinct from the hand-over status pills above (GAP-015). */
  const [printStatusFilter, setPrintStatusFilter] = useState<string | undefined>(undefined);

  // ── Detail / form panel ───────────────────────────────────────────────────
  const [panelOpen,  setPanelOpen]  = useState(false);
  const [panelMode,  setPanelMode]  = useState<'view' | 'new'>('view');
  const [detailItem, setDetailItem] = useState<ChequeIssuanceDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // New-issuance form state
  const [newForm, setNewForm] = useState({
    chequeRecordId:        '',
    issuanceDate:          new Date().toISOString().split('T')[0],
    collectorName:         '',
    collectorIdType:       '',
    collectorIdNumber:     '',
    collectorPhone:        '',
    collectorRelationship: '',
    witnessName:           '',
    witnessIdNumber:       '',
    remarks:               '',
  });
  const [saving,      setSaving]      = useState(false);
  const [actionBusy,  setActionBusy]  = useState(false);
  const [toast,       setToast]       = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Cheques we have written that nobody has collected yet. One list, used both for the picker and
  // for the count in the header, so the two cannot tell the operator different things.
  const [awaiting, setAwaiting] = useState<ChequeAwaitingIssuance[]>([]);

  // Narrows the picker below. Client-side because /cheque-issuances/awaiting returns the whole
  // queue in one call — there is no page to miss a match on, so a round trip would buy nothing.
  const [chequeSearch, setChequeSearch] = useState('');

  // Action modals
  const [showAckModal,    setShowAckModal]    = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [signatureRef,    setSignatureRef]    = useState('');
  const [cancelReason,    setCancelReason]    = useState('');
  const [collectedAt,     setCollectedAt]     = useState('');

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // Refreshed whenever an issuance is created or cancelled, since both change what is still
  // waiting to be collected.
  const loadAwaiting = useCallback(() => {
    if (!companyId) return;
    chequeIssuanceApi.getAwaiting()
      .then(r => setAwaiting(r.data.data ?? []))
      .catch(() => setAwaiting([]));
  }, [companyId]);

  useEffect(() => { loadAwaiting(); }, [loadAwaiting]);

  // ── Load list ─────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    const filter: ChequeIssuanceFilterDto = {
      companyId,
      issuanceStatus:  statusFilter,
      issuanceDateFrom: dateFrom  || undefined,
      issuanceDateTo:   dateTo    || undefined,
      collectorName:    collectorSearch || undefined,
      search:           search || undefined,
      printStatus:      printStatusFilter,
      page,
      pageSize,
    };
    try {
      const res  = await chequeIssuanceApi.getList(filter);
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(data?.totalCount ?? 0);
    } catch (err) {
      console.error('Failed to load cheque issuances', err);
    } finally {
      setLoading(false);
    }
  }, [companyId, statusFilter, dateFrom, dateTo, collectorSearch, search, printStatusFilter, page]);

  useEffect(() => { load(); }, [load]);

  // ── Open detail ───────────────────────────────────────────────────────────
  const openDetail = async (item: ChequeIssuanceListDto) => {
    setPanelMode('view');
    setPanelOpen(true);
    setDetailItem(null);
    setDetailLoading(true);
    try {
      const res = await chequeIssuanceApi.getById(item.id);
      setDetailItem(res.data?.data ?? null);
    } catch (err) {
      console.error('Failed to load issuance detail', err);
      setToast({ message: 'Failed to load detail', type: 'error' });
    } finally {
      setDetailLoading(false);
    }
  };

  /**
   * The cheques the picker offers, narrowed by the search box above it.
   *
   * Matches cheque number, payee and reference document — the same three the list's own search
   * uses, so the two boxes on this screen do not answer the same words differently.
   *
   * A cheque that is already selected stays in the list even when it no longer matches. Dropping it
   * would leave the select showing nothing while the form still holds its id, so the operator would
   * be looking at an empty picker and a Create button that works.
   */
  const awaitingOptions = useMemo(() => {
    const term = chequeSearch.trim().toLowerCase();
    if (!term) return awaiting;

    return awaiting.filter(c =>
      c.chequeRecordId === newForm.chequeRecordId ||
      c.chequeNumber.toLowerCase().includes(term) ||
      (c.payeeName ?? '').toLowerCase().includes(term) ||
      (c.referenceDocument ?? '').toLowerCase().includes(term));
  }, [awaiting, chequeSearch, newForm.chequeRecordId]);

  const openNew = () => {
    setNewForm({
      chequeRecordId:        '',
      issuanceDate:          new Date().toISOString().split('T')[0],
      collectorName:         '',
      collectorIdType:       '',
      collectorIdNumber:     '',
      collectorPhone:        '',
      collectorRelationship: '',
      witnessName:           '',
      witnessIdNumber:       '',
      remarks:               '',
    });
    setChequeSearch('');
    setPanelMode('new');
    setPanelOpen(true);
  };

  // ── Create ────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!newForm.chequeRecordId.trim() || !newForm.collectorName.trim()) {
      setToast({ message: 'Choose a cheque and enter the collector’s name.', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      await chequeIssuanceApi.create({
        chequeRecordId:        newForm.chequeRecordId,
        issuanceDate:          newForm.issuanceDate || undefined,
        collectorName:         newForm.collectorName,
        collectorIdType:       newForm.collectorIdType       || undefined,
        collectorIdNumber:     newForm.collectorIdNumber     || undefined,
        collectorPhone:        newForm.collectorPhone        || undefined,
        collectorRelationship: newForm.collectorRelationship || undefined,
        witnessName:           newForm.witnessName           || undefined,
        witnessIdNumber:       newForm.witnessIdNumber       || undefined,
        remarks:               newForm.remarks               || undefined,
      });
      setToast({ message: 'Cheque issuance created', type: 'success' });
      setPanelOpen(false);
      await load();
      loadAwaiting();
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to create issuance', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Acknowledge ───────────────────────────────────────────────────────────
  const handleAcknowledge = async () => {
    if (!detailItem) return;
    setActionBusy(true);
    try {
      const res = await chequeIssuanceApi.acknowledge(detailItem.id, {
        signatureReference: signatureRef || undefined,
      });
      setDetailItem(res.data?.data ?? detailItem);
      setToast({ message: 'Issuance acknowledged', type: 'success' });
      setShowAckModal(false);
      setSignatureRef('');
      await load();
      loadAwaiting();
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to acknowledge', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  // ── Mark collected ────────────────────────────────────────────────────────
  const handleMarkCollected = async () => {
    if (!detailItem) return;
    setActionBusy(true);
    try {
      await chequeIssuanceApi.markCollected(detailItem.id, {
        collectedAt: collectedAt || undefined,
      });
      // The endpoint returns no body, so re-read the record: otherwise the panel keeps showing the
      // status it had before the action the operator just took.
      const fresh = await chequeIssuanceApi.getById(detailItem.id);
      setDetailItem(fresh.data?.data ?? detailItem);
      setToast({ message: 'Marked as collected', type: 'success' });
      setShowCollectModal(false);
      setCollectedAt('');
      await load();
      loadAwaiting();
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to mark collected', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  // ── Cancel ────────────────────────────────────────────────────────────────
  const handleCancel = async () => {
    if (!detailItem || !cancelReason.trim()) {
      setToast({ message: 'A reason is required to cancel.', type: 'error' });
      return;
    }
    setActionBusy(true);
    try {
      await chequeIssuanceApi.cancel(detailItem.id, { reason: cancelReason });
      setToast({ message: 'Issuance cancelled', type: 'success' });
      setShowCancelModal(false);
      setCancelReason('');
      setPanelOpen(false);
      await load();
      loadAwaiting();
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to cancel', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const totalPages   = Math.ceil(total / pageSize);
  const statuses     = ['Pending', 'Issued', 'Collected', 'Cancelled'];

  return (
    <div className="flex h-full bg-slate-50 dark:bg-slate-900 overflow-hidden">

      {/* ── Main list panel ───────────────────────────────────────────────── */}
      <div className={cn('flex flex-col flex-1 overflow-hidden transition-all', panelOpen && 'mr-0')}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <FileSignature size={20} className="text-teal-600 dark:text-teal-400" />
                Cheque Issuance Register
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                {total.toLocaleString()} handed over
                {awaiting.length > 0 && (
                  <>
                    {' · '}
                    <span className="font-medium text-amber-700 dark:text-amber-400">
                      {awaiting.filter(c => c.printCount > 0).length} printed and waiting to be collected
                    </span>
                    {awaiting.some(c => c.printCount === 0) && (
                      <span className="text-slate-400">
                        {' · '}{awaiting.filter(c => c.printCount === 0).length} not yet printed
                      </span>
                    )}
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={load}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
                title="Refresh"
              >
                <RefreshCw size={16} />
              </button>
              {canCreate && (
                <button
                  onClick={openNew}
                  className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Plus size={15} />
                  New Issuance
                </button>
              )}
            </div>
          </div>

          {/* ── Status filter pills ─────────────────────────────────────────── */}
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <button
              onClick={() => { setStatusFilter(undefined); setPage(1); }}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-full transition-all',
                statusFilter === undefined
                  ? 'bg-teal-600 text-white'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200',
              )}
            >
              All
            </button>
            {statuses.map(s => (
              <button
                key={s}
                onClick={() => { setStatusFilter(prev => prev === s ? undefined : s); setPage(1); }}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-full transition-all',
                  statusFilter === s
                    ? (CHEQUE_ISSUANCE_STATUS_COLORS[s] ?? '') + ' ring-1 ring-current'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600',
                )}
              >
                {CHEQUE_ISSUANCE_STATUS_LABELS[s] ?? s}
              </button>
            ))}
          </div>

          {/* ── Print status pills (GAP-015) — separate dimension from hand-over status above:
              whether the physical cheque has been printed, not who has it. */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">Print:</span>
            {([
              [undefined,      'All'],
              ['NOT_PRINTED',  'Pending Print'],
              ['PRINTED',      'Printed / Completed'],
            ] as const).map(([value, label]) => (
              <button key={label}
                onClick={() => { setPrintStatusFilter(value); setPage(1); }}
                className={cn(
                  'px-3 py-1 text-xs font-medium rounded-full transition-all',
                  printStatusFilter === value
                    ? 'bg-teal-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {/* ── Search & date filters ─────────────────────────────────────── */}
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <div className="relative flex-1 max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={collectorSearch}
                onChange={e => { setCollectorSearch(e.target.value); setPage(1); }}
                placeholder="Search collector name…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                           bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                           focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
              />
            </div>
            <div className="relative flex-1 max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search cheque #, payee or voucher…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                           bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                           focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
              />
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <label className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">From</label>
              <input
                type="date" value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1.5
                           bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-teal-500 outline-none"
              />
              <label className="text-xs text-slate-500 dark:text-slate-400">To</label>
              <input
                type="date" value={dateTo}
                onChange={e => { setDateTo(e.target.value); setPage(1); }}
                className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1.5
                           bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-teal-500 outline-none"
              />
            </div>
          </div>
        </div>

        {/* ── Table ────────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Spinner size="lg" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <FileSignature size={40} className="mb-3 opacity-40" />
              <p className="text-sm font-medium">No cheque issuances found</p>
              {canCreate && (
                <button
                  onClick={openNew}
                  className="mt-3 text-teal-600 dark:text-teal-400 text-sm hover:underline"
                >
                  Create your first issuance
                </button>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Issuance #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Cheque #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Voucher</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Bank Account</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Payee</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Print</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Collector</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Collector ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Acknowledged</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.map(item => (
                  <tr
                    key={item.id}
                    onClick={() => openDetail(item)}
                    className="hover:bg-teal-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors group"
                  >
                    <td className="px-4 py-3 font-mono font-semibold text-teal-700 dark:text-teal-400 group-hover:text-teal-800">
                      {item.issuanceNumber}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700 dark:text-slate-300">
                      {item.chequeNumber}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-gray-400">
                      {item.referenceDocument ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700 dark:text-slate-300">
                      {item.bankAccountCode ?? '—'}
                      {item.bankAccountName && <div className="text-slate-400 dark:text-slate-500">{item.bankAccountName}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700 dark:text-slate-300 truncate max-w-[160px]">
                      {item.payeeName ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-900 dark:text-white">
                      {item.amount != null
                        ? `${item.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} ${item.currencyCode ?? ''}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-[11px] font-medium',
                        item.printCount > 0
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                          : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
                      )}>
                        {item.printCount > 0 ? `Printed ×${item.printCount}` : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-900 dark:text-white font-medium">
                      {item.collectorName}
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                      {item.collectorIdNumber ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-slate-300 whitespace-nowrap">
                      {fmtDate(item.issuanceDate)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1.5">
                        <StatusIcon status={item.issuanceStatus} />
                        <span className={cn(
                          'px-2 py-0.5 rounded-full text-xs font-medium',
                          CHEQUE_ISSUANCE_STATUS_COLORS[item.issuanceStatus] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400',
                        )}>
                          {CHEQUE_ISSUANCE_STATUS_LABELS[item.issuanceStatus] ?? item.issuanceStatus}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.isAcknowledged ? (
                        <CheckCircle2 size={16} className="text-green-500 mx-auto" />
                      ) : (
                        <Clock size={16} className="text-slate-300 mx-auto" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Pagination ───────────────────────────────────────────────────── */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
            <p className="text-sm text-slate-500 dark:text-gray-400">
              Page {page} of {totalPages} · {total.toLocaleString()} records
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const n = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                return (
                  <button
                    key={n} onClick={() => setPage(n)}
                    className={cn(
                      'w-8 h-8 text-sm rounded transition-colors',
                      n === page
                        ? 'bg-teal-600 text-white font-semibold'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300',
                    )}
                  >
                    {n}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Side panel: New form OR Detail view ───────────────────────────── */}
      {panelOpen && (
        <div className="w-[420px] flex-shrink-0 flex flex-col bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 overflow-hidden">

          {/* Panel header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">
              {panelMode === 'new' ? 'New Cheque Issuance' : 'Issuance Detail'}
            </h2>
            <button
              onClick={() => setPanelOpen(false)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Panel body */}
          <div className="flex-1 overflow-auto p-5">
            {panelMode === 'new' ? (
              /* ── New issuance form ─────────────────────────────────────── */
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Cheque <span className="text-red-500">*</span>
                  </label>

                  {/* Locating one cheque in a long hand-over queue by scrolling the dropdown is the
                      thing this box exists to avoid. Hidden only when the queue is empty, where it
                      would offer to search nothing. */}
                  {awaiting.length > 0 && (
                    <div className="relative mb-2">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        value={chequeSearch}
                        onChange={e => setChequeSearch(e.target.value)}
                        placeholder="Search cheque #, payee or voucher…"
                        aria-label="Search cheques awaiting issuance"
                        className="w-full pl-9 pr-8 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                                   bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                                   focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                      />
                      {chequeSearch && (
                        <button
                          type="button"
                          onClick={() => setChequeSearch('')}
                          aria-label="Clear cheque search"
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded
                                     text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  )}

                  <select
                    value={newForm.chequeRecordId}
                    onChange={e => setNewForm(f => ({ ...f, chequeRecordId: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                               focus:ring-2 focus:ring-teal-500 outline-none"
                  >
                    <option value="">
                      {awaiting.length === 0
                        ? 'No cheques are waiting to be collected'
                        : awaitingOptions.length === 0
                          ? 'No cheque matches that search'
                          : 'Choose the cheque being handed over…'}
                    </option>
                    {awaitingOptions.map(c => (
                      <option key={c.chequeRecordId} value={c.chequeRecordId}>
                        {c.chequeNumber} · {c.payeeName || '—'} · {c.currencyCode}{' '}
                        {c.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        {c.printCount === 0 ? ' · not yet printed' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">
                    {chequeSearch.trim() && (
                      <>
                        Showing {awaitingOptions.length} of {awaiting.length} waiting.{' '}
                      </>
                    )}
                    Only cheques you have written and not yet handed over appear here. A cheque
                    marked “not yet printed” has no paper to give anyone — print it first.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Issuance Date</label>
                  <input
                    type="date" value={newForm.issuanceDate}
                    onChange={e => setNewForm(f => ({ ...f, issuanceDate: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                               focus:ring-2 focus:ring-teal-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Collector Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={newForm.collectorName}
                    onChange={e => setNewForm(f => ({ ...f, collectorName: e.target.value }))}
                    placeholder="Full name of collector"
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                               focus:ring-2 focus:ring-teal-500 outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">ID Type</label>
                    <select
                      value={newForm.collectorIdType}
                      onChange={e => setNewForm(f => ({ ...f, collectorIdType: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                                 bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                                 focus:ring-2 focus:ring-teal-500 outline-none"
                    >
                      <option value="">— Select —</option>
                      {ID_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">ID Number</label>
                    <input
                      value={newForm.collectorIdNumber}
                      onChange={e => setNewForm(f => ({ ...f, collectorIdNumber: e.target.value }))}
                      placeholder="ID number"
                      className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                                 bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                                 focus:ring-2 focus:ring-teal-500 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Phone</label>
                    <input
                      value={newForm.collectorPhone}
                      onChange={e => setNewForm(f => ({ ...f, collectorPhone: e.target.value }))}
                      placeholder="Optional"
                      className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                                 bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                                 focus:ring-2 focus:ring-teal-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Relationship</label>
                    <input
                      value={newForm.collectorRelationship}
                      onChange={e => setNewForm(f => ({ ...f, collectorRelationship: e.target.value }))}
                      placeholder="e.g. Spouse"
                      className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                                 bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                                 focus:ring-2 focus:ring-teal-500 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Witness Name</label>
                    <input
                      value={newForm.witnessName}
                      onChange={e => setNewForm(f => ({ ...f, witnessName: e.target.value }))}
                      placeholder="Optional"
                      className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                                 bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                                 focus:ring-2 focus:ring-teal-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Witness ID</label>
                    <input
                      value={newForm.witnessIdNumber}
                      onChange={e => setNewForm(f => ({ ...f, witnessIdNumber: e.target.value }))}
                      placeholder="Optional"
                      className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                                 bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                                 focus:ring-2 focus:ring-teal-500 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Remarks</label>
                  <textarea
                    value={newForm.remarks}
                    onChange={e => setNewForm(f => ({ ...f, remarks: e.target.value }))}
                    rows={3}
                    placeholder="Optional remarks…"
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                               focus:ring-2 focus:ring-teal-500 outline-none resize-none"
                  />
                </div>
              </div>
            ) : (
              /* ── Detail view ────────────────────────────────────────────── */
              detailLoading ? (
                <div className="flex items-center justify-center h-48">
                  <Spinner size="lg" />
                </div>
              ) : !detailItem ? (
                <p className="text-sm text-slate-400 text-center py-12">Could not load detail.</p>
              ) : (
                <div className="space-y-4">
                  {/* Status + acknowledged badge */}
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      'px-2.5 py-1 rounded-full text-xs font-semibold',
                      CHEQUE_ISSUANCE_STATUS_COLORS[detailItem.issuanceStatus] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400',
                    )}>
                      {CHEQUE_ISSUANCE_STATUS_LABELS[detailItem.issuanceStatus] ?? detailItem.issuanceStatus}
                    </span>
                    {detailItem.isAcknowledged && (
                      <span className="flex items-center gap-1 px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-semibold">
                        <CheckCircle2 size={11} />
                        Acknowledged
                      </span>
                    )}
                  </div>

                  {/* Fields */}
                  {([
                    { label: 'Issuance #',     value: detailItem.issuanceNumber },
                    { label: 'Issuance Date',   value: fmtDate(detailItem.issuanceDate) },
                    { label: 'Issued By',       value: detailItem.issuedByName },
                    { label: 'Collector',       value: detailItem.collectorName },
                    { label: 'ID Type',         value: detailItem.collectorIdType ?? '—' },
                    { label: 'ID Number',       value: detailItem.collectorIdNumber ?? '—' },
                    { label: 'Phone',           value: detailItem.collectorPhone ?? '—' },
                    { label: 'Relationship',    value: detailItem.collectorRelationship ?? '—' },
                    { label: 'Witness',         value: detailItem.witnessName ?? '—' },
                    { label: 'Witness ID',      value: detailItem.witnessIdNumber ?? '—' },
                  ]).map(({ label, value }) => (
                    <div key={label} className="flex justify-between text-sm border-b border-slate-100 dark:border-slate-700 pb-2">
                      <span className="text-slate-500 dark:text-slate-400">{label}</span>
                      <span className="font-medium text-slate-900 dark:text-white text-right max-w-[200px] truncate">
                        {value}
                      </span>
                    </div>
                  ))}

                  {detailItem.signatureReference && (
                    <div className="flex justify-between text-sm border-b border-slate-100 dark:border-slate-700 pb-2">
                      <span className="text-slate-500 dark:text-slate-400">Signature Ref</span>
                      <span className="font-medium text-slate-900 dark:text-white">{detailItem.signatureReference}</span>
                    </div>
                  )}

                  {detailItem.collectedAt && (
                    <div className="flex justify-between text-sm border-b border-slate-100 dark:border-slate-700 pb-2">
                      <span className="text-slate-500 dark:text-slate-400">Collected At</span>
                      <span className="font-medium text-slate-900 dark:text-white">{fmtDate(detailItem.collectedAt)}</span>
                    </div>
                  )}

                  {detailItem.cancellationReason && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-xs text-red-700 dark:text-red-400">
                      <strong>Cancellation Reason:</strong> {detailItem.cancellationReason}
                    </div>
                  )}

                  {detailItem.remarks && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg text-xs text-slate-600 dark:text-slate-300">
                      <strong>Remarks:</strong> {detailItem.remarks}
                    </div>
                  )}
                </div>
              )
            )}
          </div>

          {/* Panel footer actions */}
          <div className="border-t border-slate-200 dark:border-slate-700 px-5 py-3 flex flex-col gap-2">
            {panelMode === 'new' ? (
              <div className="flex gap-2">
                <button
                  onClick={() => setPanelOpen(false)}
                  className="flex-1 px-4 py-2 text-sm text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
                >
                  {saving ? <Spinner size="sm" /> : <Save size={14} />}
                  Create
                </button>
              </div>
            ) : detailItem ? (
              <div className="flex flex-wrap gap-2">
                {/* Acknowledge */}
                {!detailItem.isAcknowledged && detailItem.issuanceStatus !== 'Cancelled' && (
                  <button
                    onClick={() => setShowAckModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    <CheckCircle2 size={12} />
                    Acknowledge Receipt
                  </button>
                )}
                {/* Mark Collected */}
                {detailItem.isAcknowledged && detailItem.issuanceStatus !== 'Collected' && detailItem.issuanceStatus !== 'Cancelled' && (
                  <button
                    onClick={() => setShowCollectModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                  >
                    <CheckCircle2 size={12} />
                    Mark Collected
                  </button>
                )}
                {/* Cancel */}
                {detailItem.issuanceStatus !== 'Cancelled' && detailItem.issuanceStatus !== 'Collected' && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <XCircle size={12} />
                    Cancel
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* ── Toast ────────────────────────────────────────────────────────────── */}
      {toast && (
        <div className={cn(
          'fixed bottom-5 right-5 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium z-50',
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
        )}>
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          {toast.message}
        </div>
      )}

      {/* ── Acknowledge modal ─────────────────────────────────────────────────── */}
      {showAckModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Acknowledge Receipt</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Optionally provide a signature reference.
            </p>
            <input
              value={signatureRef}
              onChange={e => setSignatureRef(e.target.value)}
              placeholder="Signature reference (optional)"
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 outline-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowAckModal(false)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAcknowledge} disabled={actionBusy}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
              >
                {actionBusy ? <Spinner size="sm" /> : <CheckCircle2 size={14} />}
                Acknowledge
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mark Collected modal ──────────────────────────────────────────────── */}
      {showCollectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Mark as Collected</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Optionally record the exact collection date/time.
            </p>
            <input
              type="datetime-local"
              value={collectedAt}
              onChange={e => setCollectedAt(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 outline-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowCollectModal(false)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkCollected} disabled={actionBusy}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
              >
                {actionBusy ? <Spinner size="sm" /> : <CheckCircle2 size={14} />}
                Mark Collected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cancel modal ─────────────────────────────────────────────────────── */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Cancel Issuance</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Provide a reason for cancellation.</p>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Reason for cancellation…"
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 outline-none resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Keep
              </button>
              <button
                onClick={handleCancel} disabled={actionBusy || !cancelReason.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
              >
                {actionBusy ? <Spinner size="sm" /> : <XCircle size={14} />}
                Cancel Issuance
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
